/**
 * The countries VozQI is sold in, as a table, and which one is ACTIVE.
 *
 * Each country is a row here, and the business carries a `country` column.
 * What every row carries is what BOTH SIDES enforce: the phone and the
 * web app format a balboa or a sol the same way, and the server refuses a
 * phone number that does not have that country's digit count.
 *
 * TWO WAYS TO READ IT, and which one depends on who is asking.
 *
 * A CLIENT shows one business at a time, so it sets the active locale once,
 * when the business profile loads, and every formatter reads it as its
 * default argument. That is why the forty-odd screens that call
 * `formatMoney` never learnt a second parameter: `LOCALE.currencySymbol` in a
 * field's prefix keeps reading whatever business is open.
 *
 * THE SERVER serves every business at once, so it must NEVER call
 * `setLocale`: a global there is a Peruvian phone validated against a
 * Panamanian rule because another request set it a millisecond earlier. The
 * server passes `localeOf(business.country)` explicitly, every time.
 *
 * NOT HERE: the timezone, which is the business's own (`timezone.ts`).
 *
 * «CENTS» IS THE COLUMN'S NAME, NOT ALWAYS ITS UNIT. Every amount is stored
 * as an integer in the currency's MINOR unit: centésimos for a balboa,
 * céntimos for a sol, and for a peso — which has no minor unit anybody
 * writes — the peso itself. `decimals` says which, and money.ts is the only
 * place that multiplies or divides by it. Storing pesos ×100 would have
 * capped an int4 column at some twenty-one million pesos, which one month
 * of a bakery's ledger walks past.
 */
import { COMPANY_ONLY_TAX_ID_KINDS } from "./models.js";
export const COUNTRY_CODES = ["PA", "CO", "CR", "GT", "MX", "PE", "EC", "UY", "VE", "US"];
export const COUNTRIES = {
    PA: {
        code: "PA",
        name: "Panamá",
        currencyCode: "PAB",
        currencySymbol: "B/.",
        phoneCountryCode: "507",
        phoneDigits: 8,
        decimals: 2,
        thousandsSeparator: ",",
        decimalSeparator: ".",
        extraPaymentMethods: ["YAPPY"],
        fiscalAuthority: "DGI",
        taxIdKinds: ["CEDULA", "RUC", "PASAPORTE"],
        addressLabels: { line: "Dirección", neighborhood: "Corregimiento", locality: "Distrito", region: "Provincia", postalCode: null },
    },
    CO: {
        code: "CO",
        name: "Colombia",
        currencyCode: "COP",
        currencySymbol: "$",
        phoneCountryCode: "57",
        phoneDigits: 10,
        decimals: 0,
        thousandsSeparator: ".",
        decimalSeparator: ",",
        extraPaymentMethods: ["NEQUI", "DAVIPLATA"],
        fiscalAuthority: null,
        taxIdKinds: ["CEDULA", "NIT", "PASAPORTE"],
        addressLabels: { line: "Dirección", neighborhood: "Barrio", locality: "Municipio", region: "Departamento", postalCode: null },
    },
    CR: {
        code: "CR",
        name: "Costa Rica",
        currencyCode: "CRC",
        currencySymbol: "₡",
        phoneCountryCode: "506",
        phoneDigits: 8,
        decimals: 2,
        thousandsSeparator: ",",
        decimalSeparator: ".",
        extraPaymentMethods: ["SINPE_MOVIL"],
        fiscalAuthority: null,
        taxIdKinds: ["CEDULA", "CEDULA_JURIDICA", "PASAPORTE"],
        addressLabels: { line: "Otras señas", neighborhood: "Distrito", locality: "Cantón", region: "Provincia", postalCode: null },
    },
    GT: {
        code: "GT",
        name: "Guatemala",
        currencyCode: "GTQ",
        currencySymbol: "Q",
        phoneCountryCode: "502",
        phoneDigits: 8,
        decimals: 2,
        thousandsSeparator: ",",
        decimalSeparator: ".",
        extraPaymentMethods: [],
        fiscalAuthority: null,
        taxIdKinds: ["DPI", "NIT", "PASAPORTE"],
        addressLabels: { line: "Dirección", neighborhood: "Zona", locality: "Municipio", region: "Departamento", postalCode: null },
    },
    MX: {
        code: "MX",
        name: "México",
        currencyCode: "MXN",
        currencySymbol: "$",
        phoneCountryCode: "52",
        phoneDigits: 10,
        decimals: 2,
        thousandsSeparator: ",",
        decimalSeparator: ".",
        extraPaymentMethods: [],
        fiscalAuthority: null,
        taxIdKinds: ["RFC", "CURP", "PASAPORTE"],
        addressLabels: { line: "Calle y número", neighborhood: "Colonia", locality: "Municipio o alcaldía", region: "Estado", postalCode: "Código postal" },
    },
    PE: {
        code: "PE",
        name: "Perú",
        currencyCode: "PEN",
        currencySymbol: "S/",
        phoneCountryCode: "51",
        phoneDigits: 9,
        decimals: 2,
        thousandsSeparator: ",",
        decimalSeparator: ".",
        extraPaymentMethods: ["YAPE", "PLIN"],
        fiscalAuthority: null,
        taxIdKinds: ["DNI", "RUC", "PASAPORTE"],
        addressLabels: { line: "Dirección", neighborhood: "Distrito", locality: "Provincia", region: "Departamento", postalCode: null },
    },
    EC: {
        code: "EC",
        name: "Ecuador",
        currencyCode: "USD",
        currencySymbol: "$",
        phoneCountryCode: "593",
        phoneDigits: 9,
        decimals: 2,
        thousandsSeparator: ",",
        decimalSeparator: ".",
        extraPaymentMethods: [],
        fiscalAuthority: null,
        taxIdKinds: ["CEDULA", "RUC", "PASAPORTE"],
        addressLabels: { line: "Dirección", neighborhood: "Parroquia", locality: "Cantón", region: "Provincia", postalCode: null },
    },
    // The Spanish-speaking market in the United States, not the country at
    // large. Same shape as any other row --
    // the app stays in Spanish, and the wallets are the ones people there
    // actually send money with.
    US: {
        code: "US",
        name: "Estados Unidos",
        currencyCode: "USD",
        currencySymbol: "$",
        phoneCountryCode: "1",
        phoneDigits: 10,
        decimals: 2,
        thousandsSeparator: ",",
        decimalSeparator: ".",
        extraPaymentMethods: ["ZELLE", "VENMO"],
        fiscalAuthority: null,
        taxIdKinds: ["SSN", "EIN", "PASAPORTE"],
        addressLabels: { line: "Dirección", neighborhood: "Barrio", locality: "Ciudad", region: "Estado", postalCode: "Código postal" },
    },
    UY: {
        code: "UY",
        name: "Uruguay",
        currencyCode: "UYU",
        // «$» on its own is already Mexico's and the United States'; «$U» is how
        // the peso uruguayo is written on a price tag there.
        currencySymbol: "$U",
        phoneCountryCode: "598",
        // The mobile without the trunk zero people write in front of it: 9X XXX XXX.
        phoneDigits: 8,
        // ZERO, and this DECIDES THE UNIT MONEY IS STORED IN -- changing it later
        // would need a data migration, not an edit. This file's own header states
        // the rule: «for a peso -- which has no minor unit anybody writes -- the
        // peso itself». The centésimo does not circulate in Uruguay and prices are
        // written whole, so 1250 pesos are the integer 1250. Same call as Colombia.
        decimals: 0,
        thousandsSeparator: ".",
        decimalSeparator: ",",
        // The mobile is written «99 123 456», not «9912-3456»: eight digits are
        // [4, 4] for Panama and Costa Rica and wrong here, so this row carries its
        // own grouping and the by-length default stays everybody else's.
        phoneGrouping: { sizes: [2, 3, 3], separator: " " },
        // No wallet of its own. A new payment method is the one
        // thing on this row that WOULD need a migration (`cash_entry_method_check`).
        extraPaymentMethods: [],
        fiscalAuthority: null,
        taxIdKinds: ["CEDULA", "RUT", "PASAPORTE"],
        addressLabels: { line: "Dirección", neighborhood: "Barrio", locality: "Ciudad", region: "Departamento", postalCode: null },
    },
    VE: {
        code: "VE",
        name: "Venezuela",
        currencyCode: "VES",
        currencySymbol: "Bs.",
        phoneCountryCode: "58",
        phoneDigits: 10,
        decimals: 2,
        thousandsSeparator: ".",
        decimalSeparator: ",",
        extraPaymentMethods: ["PAGO_MOVIL"],
        fiscalAuthority: null,
        taxIdKinds: ["CEDULA", "RIF", "PASAPORTE"],
        addressLabels: { line: "Dirección", neighborhood: "Parroquia", locality: "Municipio", region: "Estado", postalCode: null },
    },
};
/** The documents a PERSON of this country may carry -- the country's list, less the company-only ones. */
export function personTaxIdKinds(country) {
    return COUNTRIES[country].taxIdKinds.filter((kind) => !COMPANY_ONLY_TAX_ID_KINDS.includes(kind));
}
/** Every business created before the column existed, and the sample ones. */
export const DEFAULT_COUNTRY = "PA";
function isCountryCode(value) {
    return typeof value === "string" && COUNTRY_CODES.includes(value);
}
/**
 * The row for a stored code. A code not on the list — a business whose
 * country predates a shorter list, or a row written by hand — reads as
 * Panama rather than crashing every screen: wrong symbol beats no app.
 */
export function localeOf(code) {
    return isCountryCode(code) ? COUNTRIES[code] : COUNTRIES[DEFAULT_COUNTRY];
}
/** What the wizard's picker offers, alphabetically -- a list, not a guess about where the next owner is. */
export function countryOptions() {
    // `.sort` on the array `.map` just made, NOT `.toSorted`: Hermes, the
    // phone's engine, implements ES2022 and has no `toSorted` (nor `toReversed`,
    // `with`, `findLast`): the wizard dies with «undefined is not a function» on
    // an iPhone while every browser and this package's own tests run it fine.
    // The test in
    // locale.test.ts takes the method away the way Hermes never had it.
    return COUNTRY_CODES.map((value) => ({ value, label: COUNTRIES[value].name })).sort((a, b) => a.label.localeCompare(b.label, "es"));
}
/** Whether «Escanear factura» has anything to read in this country. */
export function supportsInvoiceScan(country = getLocale()) {
    return country.fiscalAuthority !== null;
}
let active = COUNTRIES[DEFAULT_COUNTRY];
/** The business currently open, as far as the formatters are concerned. Clients only. */
export function getLocale() {
    return active;
}
/**
 * Called once by each client when the business profile loads, and again when
 * the owner switches business. THROWS on an unknown code rather than
 * silently staying on the previous business's country: a client that got
 * here holds a profile the server already validated, so a bad code is a
 * bug, not an input.
 */
export function setLocale(code) {
    if (!isCountryCode(code))
        throw new Error(`Unknown country: ${String(code)}`);
    active = COUNTRIES[code];
}
/**
 * The active country's facts, read live — `LOCALE.phoneDigits` in a hint or
 * a validation message follows whatever business is open. Getters rather
 * than a snapshot so the twenty places that read this before the table
 * existed keep working unchanged.
 */
export const LOCALE = {
    get currencyCode() {
        return active.currencyCode;
    },
    get currencySymbol() {
        return active.currencySymbol;
    },
    get phoneCountryCode() {
        return active.phoneCountryCode;
    },
    get phoneDigits() {
        return active.phoneDigits;
    },
    get taxIdKinds() {
        return active.taxIdKinds;
    },
    get personTaxIdKinds() {
        return personTaxIdKinds(active.code);
    },
    /**
     * QUIÉN COBRA IMPUESTOS AQUÍ, o nadie que la app conozca.
     *
     * Expuesto para que una pantalla pueda preguntar «¿hay
     * dígito verificante?» sin escribir `country === "PA"`. El DV es de la DGI y
     * de nadie más; el día que otro país estrene uno, lo dice la tabla.
     */
    get fiscalAuthority() {
        return active.fiscalAuthority;
    },
    /** What the country is called, for the locked «País» of an address. */
    get name() {
        return active.name;
    },
    /** What each field of a client's address is called here (0057). */
    get addressLabels() {
        return active.addressLabels;
    },
};
